import { cn } from "@/lib/cn";

/**
 * A section's status as one glyph, the same in the rail, the brief and the
 * list: empty · needs more · clear · not sure. Colours are tokens, so the PDF
 * and the UI agree.
 */
export type SectionStatus = "empty" | "needs_more" | "clear" | "not_sure" | "unassessed";

export const STATUS_LABEL: Record<SectionStatus, string> = {
  empty: "Empty",
  needs_more: "Needs more",
  clear: "Clear",
  not_sure: "Not sure, and that's fine",
  unassessed: "Written, not checked yet",
};

export function statusOf(section: {
  content: string;
  state: "empty" | "draft" | "not_sure" | "done";
  assessment: { status: "clear" | "needs_more" | "unassessed" } | null;
  assessmentStale: boolean;
}): SectionStatus {
  if (section.state === "not_sure") return "not_sure";
  if (section.content.trim().length === 0) return "empty";
  if (!section.assessment || section.assessmentStale || section.assessment.status === "unassessed") {
    return "unassessed";
  }
  return section.assessment.status;
}

export function StatusDot({ status, className }: { status: SectionStatus; className?: string }) {
  const base = "inline-block h-2.5 w-2.5 shrink-0 rounded-full border";
  switch (status) {
    case "empty":
      return <span className={cn(base, "border-status-empty bg-transparent", className)} aria-label="Empty" />;
    case "unassessed":
      return <span className={cn(base, "border-status-empty bg-status-empty", className)} aria-label="Written" />;
    case "needs_more":
      return (
        <span
          className={cn(base, "border-status-needs-more", className)}
          style={{ background: "linear-gradient(90deg, var(--status-needs-more) 50%, transparent 50%)" }}
          aria-label="Needs more"
        />
      );
    case "clear":
      return <span className={cn(base, "border-status-clear bg-status-clear", className)} aria-label="Clear" />;
    case "not_sure":
      return (
        <span
          className={cn("text-status-not-sure inline-block w-2.5 text-center text-[10px] leading-none font-bold", className)}
          aria-label="Not sure"
        >
          ?
        </span>
      );
  }
}
